<script>
   /*************************************************************/
   /* PlotFoM                                                   */
   /* --------------------                                      */
   /* shows plot with figure of merit values vs. number of PCs  */
   /*************************************************************/

   import { vector } from 'mdatools/arrays';
   import { Legend, Segments } from 'svelte-plots-basic/2d';
   import { min, max } from 'mdatools/stat';

   import { LINE_COLORS, FACE_COLORS, MARKERS, getResLegend } from './Defaults.js';
   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';
   import { csvRow, downloadCSV } from '../utils.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // full names of the foms
   const titles = {
      'rmse': 'RMSE',
      'r2': 'R^2',
      'slope': 'Slope'
   }

   // get the parameters needed for the plot
   let res = $derived(props ? props.res : null);
   let resIDs = $derived(res ? Object.keys(res) : null);
   let plotResIDs = $derived(params && params.resID && resIDs ? (params.resID === 'both' ? resIDs : [params.resID]) : null);
   let resobj = $derived(plotResIDs ? res[plotResIDs[0]] : null);

   // plot parameters
   let yLabel = $derived(params ? titles[params.fom]: '');
   let title = $derived(params ? titles[params.fom] +
      (resobj.nresp > 1 ? ' (' + resobj.varlabels[params.resp - 1] + ')': '') : '');
   let xMin = $derived(resobj.ncomp > 1 ? 1 - resobj.ncomp * 0.065 : -0.1);
   let xMax = $derived(resobj.ncomp > 1 ? resobj.ncomp * 1.065 : 2.1);
   let limX = $derived([xMin, xMax]);
   let xValues = $derived(resobj ? resobj.stat.components : null);

   // plot series
   let series = $derived.by(() => {
      if (!plotResIDs) return null;

      let yMin = 0;
      let yMax = Number.NEGATIVE_INFINITY;
      let values = [];

      for (let id of plotResIDs) {
         const y = res[id].stat[params.fom].getcolref(params.resp);
         // R2 and slope are NaN for a set whose reference values are all equal: nothing to draw
         if (!y.every(Number.isFinite)) continue;
         yMin = Math.min(yMin, min(y));
         yMax = Math.max(yMax, max(y));
         values.push({
            yValues: vector(y),
            labels: Array.from(y).map(yy => yy.toFixed(yy < 0.1 ? 3 : 2)),
            faceColor: FACE_COLORS[id],
            mainColor: LINE_COLORS[id],
            marker: MARKERS[id],
            id: id
          })
      }

      return values.length > 0 ? { yMin, yMax, values } : null;
   });

   // legend
   let legendItems = $derived(getResLegend(plotResIDs));

   // y-axis limits
   const dY = $derived(series ? (series.yMax - series.yMin) * 0.05 : 0)
   const limY = $derived(series ? [series.yMin - dY, series.yMax + 4 * dY] : [0, 1]);

   // name of the image and CSV files
   const fileName = $derived('plot-' + params.fom);

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      const { sep, dec } = props.data[plotResIDs[0]];
      let out = csvRow(['Set', 'LVs', title], sep, dec);
      for (const s of series.values) {
         for (let i = 0; i < xValues.length; i++) {
            out += csvRow([s.id, xValues[i], s.yValues.v[i]], sep, dec);
         }
      }
      downloadCSV(out, fileName);
   }
</script>

{#if xValues && series }
<Axes {title} {limX} {limY} {yLabel}
   xLabel="Number of LVs, A"
   {fileName}
   {downloadCSVCallback}
   whole={true}>

   {#if props.ncomp}
   <Segments xStart={[props.ncomp]} xEnd={[props.ncomp]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor='#a0a0a0' />
   {/if}

   <!-- data points  -->
   {#each series.values as s, i }
   <PlotSeries
      {xValues}
      yValues = {s.yValues}
      labels = {s.labels}
      faceColor={s.faceColor}
      mainColor={s.mainColor}
      marker={s.marker}
      plotType='b'
      showLabels={params.showLabels}
      lineWidth={1.5}
   />
   {/each}

   <!-- data points  for selected component -->
   {#if props.ncomp}
   {#each series.values as s, i }
   <PlotSeries
      xValues={[xValues[props.ncomp - 1]]}
      yValues={[s.yValues.v[props.ncomp - 1]]}
      faceColor={s.mainColor}
      mainColor={s.mainColor}
      marker={s.marker}
      plotType='b'
      showLabels={params.showLabels}
      markerWidth={3.0}
   />
   {/each}
   {/if}

   <Legend items={legendItems} />
</Axes>
{/if}